import { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { STAUTES } from "../../../utils/constant";
import { errorToast, successToast } from "../../../utils/toastHelper";
import usePageState from "../../../utils/customHooks/usePageState";
import useColumn from "../../../utils/customHooks/useColumns";
import { DELETE_CLIENT, GET_CLIENTS } from "../../../utils/services/apiPath";
import { deleteApi, postApi } from "../../../utils/services";
import { useTheme } from "../../ContextStore/ThemeContext";
import { useDispatch, useSelector } from "react-redux";
import { handleLogout, userSelector } from "../../store/features/auth/authSlice";
import logo from "../../Assets/Icons/logo.png";
import logoWhite from "../../Assets/Icons/logo-white.png";
import menuopen from "../../Assets/Icons/menu-open.png";
import menuclose from "../../Assets/Icons/menu-close.png";
import NotificationIcon from "../../Assets/IconComponents/Notification";
import { headerMenus } from "../../../utils/MenusConfig";
import HelpMenu from "../../Components/Header/HelpMenu";
import { ProGrid, NameCell, DeleteCell, WebLinkCell, Layout } from "@proscore-ai/proscore-ui-library";
import useCsvExport from "../../../utils/useCsvExporter";

const getRedirectUrl = params => {
  return `/clients/${params.id}/profile`;
};

export default function ClientList() {
  const {
    currentPage,
    pageSize,
    data: clientsData,
    rowCount,
    status,
    sortModel,
    searchString,
    setPageState,
    setPagination,
    setStatus,
    setSortModel,
    setSearchString
  } = usePageState();
  const navigation = useNavigate();
  const columns = useColumn(
    [
      {
        field: "companyName",
        headerName: "Company Name",
        renderCell: params => <NameCell params={params} getRedirectUrl={getRedirectUrl} />
      },
      {
        field: "title",
        headerName: "Title"
      },
      {
        field: "name",
        headerName: "Name",
        valueGetter: params => `${params.row.contactFirstName || ""} ${params.row.contactLastName || ""}`
      },
      {
        field: "city",
        headerName: "City"
      },
      {
        field: "state",
        headerName: "State"
      },
      {
        field: "zip",
        headerName: "Zip"
      },
      {
        field: "website",
        headerName: "Website",
        renderCell: params => <WebLinkCell params={params} />
      },
      {
        field: "clientGuid",
        headerName: "Actions",
        isDeleteColumn: true,
        disableExport: true,
        width: 80,
        renderCell: params => <DeleteCell params={params} onDelete={onDelete} title={"Delete Client"} />
      }
    ],
    true
  );

  
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [popOverOpen, setPopOverOpen] = useState(false);
  const ref = useRef(null);
  const userData = useSelector(userSelector);
  const dispatch = useDispatch();

  const handleTabClick = tab => {
    //navigate(`${tab.to}`);
    const cleanPath = tab.to.startsWith("/") ? tab.to : `/${tab.to}`;
    navigate(cleanPath);
  };
  const handleMenuOpen = () => setMenuOpen(!menuOpen);

  const onLogout = () => {
    dispatch(handleLogout());
  };

  const manageAccount = () => {
    setPopOverOpen(false);
  };

  const handleHome = () => {
    navigate("/clients");
  };

  const onDelete = async id => {
    const { error } = await deleteApi(DELETE_CLIENT, { params: { clientId: id } });
    if (error) return errorToast(error);
    successToast("Client deleted successfully.");
    getClientList();
  };

  const addButtonAction = () => {
    navigation("/clients/add");
  };

  const getClientList = useCallback(async () => {
    setStatus(STAUTES.LOADING);
    const payload = {
      pageIndex: searchString ? 1 : currentPage + 1,
      pageSize: pageSize,
      searchString: searchString,
      orderByAscending: true,
      orderCol: "companyName",
      ...sortModel
    };
    const { data, error, totalRecords } = await postApi(GET_CLIENTS, payload);
    setStatus(STAUTES.IDLE);
    if (error) return errorToast(error);
    setPageState(prevPageInfo => ({ ...prevPageInfo, data: data || [], rowCount: totalRecords }));
  }, [currentPage, pageSize, sortModel, searchString, setPageState, setStatus]);

  useEffect(() => {
    getClientList();
  }, [getClientList]);

  const handleDownloadCSV = useCsvExport({
    api: GET_CLIENTS,

    getPayload: () => {
      const activeSort = sortModel?.[0] || {
        field: "companyName",
        sort: "asc"
      };

      return {
        pageIndex: 1,
        pageSize: rowCount || 10000,
        searchString,
        orderByAscending: activeSort.sort === "asc",
        orderCol: activeSort.field
      };
    },

    mapData: data =>
      data.map(item => ({
        "Company Name": item.companyName || "",
        "Title": item.title || "",
        "Name": `${item.contactFirstName || ""} ${item.contactLastName || ""}`,
        "City": item.city || "",
        "State": item.state || "",
        "Zip": item.zip || "",
        "Website": item.website || ""
      })),

    fileName: "ClientList.csv"
  });

  return (
    <Layout
      canView={true}
      theme={theme}
      user={{
        userName: userData?.userName,
        email: userData?.email,
        avatar: userData?.avatar
      }}
      onLogout={onLogout}
      onTabClick={handleTabClick}
      onLogoClick={handleHome}
      logo={logo}
      logoWhite={logoWhite}
      menuOpenIcon={menuopen}
      menuCloseIcon={menuclose}
      menuTabs={headerMenus}
      NotificationIcon={NotificationIcon}
      HelpComponent={HelpMenu}
    >
      <ProGrid
        title="Clients"
        loading={status === STAUTES.LOADING}
        options={{
          getRowId: row => row.clientGuid,
          rowCount: rowCount,
          paginationMode: "server",
          paginationModel: { pageSize: pageSize, page: currentPage },
          onPaginationModelChange: setPagination,
          sortingMode: "server",
          onSortModelChange: setSortModel
        }}
        columns={columns}
        hideAddButton={false}
        hideExport={false}
        rows={clientsData}
        searchMode={!!searchString}
        searchModel={{ defaultValue: searchString, handleDebounce: setSearchString }}
        addButtonLabel="+ Add Client"
        addButtonAction={addButtonAction}
        exportAction={handleDownloadCSV}
      />
    </Layout>
  );
}
